import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import PatientDiagnosisDiscussions from '../model/PatientsDiagnosisDiscussions'
import PatientDiagnosisRepository from './appointment/PatientDiagnosisRepository'
import PatientsDiagnosisTopics from 'model/PatientsDiagnosisTopics'
import PatientsDiagnosisMedications from 'model/PatientsDiagnosisMedications'
import PatientsDiagnosis from 'model/PatientsDiagnosis'
import PatientsDiagnosisSimple from 'model/PatientsDiagnosisSimple'
import PatientFile from 'model/PatientFile'
import Allergy from 'model/Allergy'
import { setExaminationDocument } from 'model/Examination'
import InternalExaminationPlan from 'model/InternalExaminationPlan'

interface PatientDiagnosisState {
  isLoadingTopics: boolean
  isLoadingExaminations: boolean
  examinationsLoaded: boolean
  isLoading: boolean
  topics: PatientsDiagnosisTopics[]
  diagnosis: PatientsDiagnosis[]
  discussions: PatientDiagnosisDiscussions[]
  medications: PatientsDiagnosisMedications[]
  allergies: Allergy[]
  examinations: any[]
  medicalReferralResults: PatientFile[]
  appointmentOwner: { _id: any; firstName: string; lastName: string } | null
  schedulePlans: InternalExaminationPlan[]
}

const initialState: PatientDiagnosisState = {
  isLoadingTopics: true,
  isLoadingExaminations: true,
  examinationsLoaded: false,
  isLoading: false,
  topics: [] as PatientsDiagnosisTopics[],
  diagnosis: [] as PatientsDiagnosis[],
  discussions: [] as PatientDiagnosisDiscussions[],
  medications: [] as PatientsDiagnosisMedications[],
  allergies: [] as Allergy[],
  examinations: [],
  medicalReferralResults: [],
  appointmentOwner: null,
  schedulePlans: [],
}

function startLoading(state: PatientDiagnosisState) {
  state.isLoading = true
}

function startLoadingTopics(state: PatientDiagnosisState) {
  state.isLoadingTopics = true
}

function startLoadingExaminations(state: PatientDiagnosisState) {
  state.isLoadingExaminations = true
}

const patientDiagnosisSlice = createSlice({
  name: 'patientDiagnosis',
  initialState,
  reducers: {
    fetchPatientTopicsStart: startLoadingTopics,
    fetchPatientDiagnosisStart: startLoading,
    fetchPatientDiscussionsStart: startLoading,
    fetchPatientMedicationsStart: startLoading,
    fetchPatientAllergiesStart: startLoading,
    fetchPatientExaminationsStart: startLoadingExaminations,
    fetchPatientMedicalReferralResultsStart: startLoading,
    createPatientTopicStart: startLoading,
    updatePatientTopicStart: startLoading,
    fetchPatientTopicsSuccess(state, { payload }: PayloadAction<PatientsDiagnosisTopics[]>) {
      state.topics = payload
      state.isLoading = false
      state.isLoadingTopics = false
    },
    fetchPatientDiagnosisSuccess(state, { payload }: PayloadAction<PatientsDiagnosis[]>) {
      state.diagnosis = payload
      state.isLoading = false
    },
    fetchPatientDiscussionsSuccess(
      state,
      { payload }: PayloadAction<PatientDiagnosisDiscussions[]>,
    ) {
      state.discussions = payload
      state.isLoading = false
    },
    fetchPatientMedicationsSuccess(
      state,
      { payload }: PayloadAction<PatientsDiagnosisMedications[]>,
    ) {
      state.medications = payload
      state.isLoading = false
    },
    fetchPatientAllergiesSuccess(state, { payload }: PayloadAction<Allergy[]>) {
      state.allergies = payload
      state.isLoading = false
    },
    fetchPatientExaminationsSuccess(state, { payload }: PayloadAction<any[]>) {
      state.examinations = payload
      state.isLoading = false
      state.isLoadingExaminations = false
      state.examinationsLoaded = true
    },
    fetchPatientMedicalReferralResultsSuccess(state, { payload }: PayloadAction<PatientFile[]>) {
      state.medicalReferralResults = payload
      state.isLoading = false
    },
    fetchPatientSchedulePlansSuccess(state, { payload }: PayloadAction<InternalExaminationPlan[]>) {
      state.schedulePlans = payload
      state.isLoading = false
    },
    fetchPatientAppointmentOwnerSuccess(state, { payload }: PayloadAction<any>) {
      state.appointmentOwner = payload
    },
    createPatientTopicSuccess(state, { payload }) {
      state.topics = [payload?.topic, ...state.topics]
      state.diagnosis = [payload?.mainDiagnosis, ...state.diagnosis]
      state.isLoading = false
    },
    createPatientSchedulePlanSuccess(state, { payload }) {
      state.schedulePlans = [payload?.schedulePlan, ...state.schedulePlans]
      state.isLoading = false
    },
    updatePatientTopicSuccess(state, { payload }) {
      state.isLoading = false
      const objIndex = state.topics.findIndex(
        topic => (topic as any)._id === payload?.updatedTopic._id,
      )
      state.topics = [
        ...state.topics.slice(0, objIndex),
        payload.updatedTopic,
        ...state.topics.slice(objIndex + 1),
      ]
      if (payload.newDiagnosis) state.diagnosis = [...state.diagnosis, payload?.newDiagnosis]
    },
    deleteTopicSuccess(state, { payload }) {
      const filteredTopics = state.topics.filter(item => item._id !== payload.id)
      state.topics = filteredTopics
    },
    updatePatientDiscussionsSuccess(state, { payload }) {
      state.isLoading = false

      const objIndex = state.discussions.findIndex(
        discussion => (discussion as any)._id === payload?._id,
      )

      let newDiscussionList =
        objIndex >= 0
          ? [
              ...state.discussions.slice(0, objIndex),
              payload,
              ...state.discussions.slice(objIndex + 1),
            ]
          : [payload, ...state.discussions]

      state.discussions = newDiscussionList
    },
    updatePatientMedicasionsSuccess(state, { payload }) {
      state.isLoading = false

      const objIndex = state.medications.findIndex(
        medication => (medication as any)._id === payload?._id,
      )

      let newMedicationsList =
        objIndex >= 0
          ? [
              ...state.medications.slice(0, objIndex),
              payload,
              ...state.medications.slice(objIndex + 1),
            ]
          : [payload, ...state.medications]

      state.medications = newMedicationsList
    },
    updatePatientAllergiesSuccess(state, { payload }) {
      state.isLoading = false

      const objIndex = state.allergies.findIndex(allergy => (allergy as any)._id === payload?._id)

      let newAllergiesList =
        objIndex >= 0
          ? [...state.allergies.slice(0, objIndex), payload, ...state.allergies.slice(objIndex + 1)]
          : [payload, ...state.allergies]

      state.allergies = newAllergiesList
    },
    updatePatientSchedulePlanSuccess(state, { payload }) {
      state.isLoading = false

      const objIndex = state.schedulePlans.findIndex(
        schedulePlan => (schedulePlan as any)._id === payload?._id,
      )

      let newSchedulePlan =
        objIndex >= 0
          ? [
              ...state.schedulePlans.slice(0, objIndex),
              payload,
              ...state.schedulePlans.slice(objIndex + 1),
            ]
          : [payload, ...state.schedulePlans]

      state.schedulePlans = newSchedulePlan
    },
    // updatePatientExaminationsSuccess(state, { payload }) {
    //   state.isLoading = false
    //   state.examinations = [payload, ...state.examinations]
    // },
    updatePatientFailed(state: PatientDiagnosisState) {
      state.isLoading = false
    },
  },
})

export const {
  fetchPatientTopicsStart,
  fetchPatientDiagnosisStart,
  fetchPatientDiscussionsStart,
  fetchPatientMedicationsStart,
  fetchPatientAllergiesStart,
  fetchPatientExaminationsStart,
  fetchPatientSchedulePlansSuccess,
  fetchPatientMedicalReferralResultsStart,
  createPatientTopicStart,
  updatePatientTopicStart,
  updatePatientSchedulePlanSuccess,
  fetchPatientTopicsSuccess,
  fetchPatientDiagnosisSuccess,
  fetchPatientDiscussionsSuccess,
  fetchPatientMedicationsSuccess,
  fetchPatientAllergiesSuccess,
  fetchPatientExaminationsSuccess,
  fetchPatientMedicalReferralResultsSuccess,
  fetchPatientAppointmentOwnerSuccess,
  createPatientTopicSuccess,
  createPatientSchedulePlanSuccess,
  updatePatientTopicSuccess,

  updatePatientDiscussionsSuccess,
  updatePatientMedicasionsSuccess,
  updatePatientAllergiesSuccess,
  deleteTopicSuccess,
  // updatePatientExaminationsSuccess,
  updatePatientFailed,
} = patientDiagnosisSlice.actions

export const fetchPatientTopics = (patientId: string): AppThunk => async dispatch => {
  dispatch(fetchPatientTopicsStart())
  const topics = await PatientDiagnosisRepository.findAllPatientTopics({ patientId })

  dispatch(fetchPatientTopicsSuccess(topics))
}

export const fetchPatientDiagnosis = (patientId: string): AppThunk => async dispatch => {
  dispatch(fetchPatientDiagnosisStart())
  const diagnosis = await PatientDiagnosisRepository.findAllPatientDiagnosis({ patientId })

  dispatch(fetchPatientDiagnosisSuccess(diagnosis))
}

export const fetchPatientDiscussions = (patientId: string): AppThunk => async dispatch => {
  dispatch(fetchPatientDiscussionsStart())
  const discussions = await PatientDiagnosisRepository.findAllPatientDiscussions({ patientId })

  dispatch(fetchPatientDiscussionsSuccess(discussions))
}

export const fetchPatientMedications = (patientId: string): AppThunk => async dispatch => {
  dispatch(fetchPatientMedicationsStart())
  const medications = await PatientDiagnosisRepository.findAllPatientMedications({ patientId })

  dispatch(fetchPatientMedicationsSuccess(medications))
}

export const fetchPatientAllergies = (patientId: string): AppThunk => async dispatch => {
  dispatch(fetchPatientAllergiesStart())
  const allergies = await PatientDiagnosisRepository.findAllPatientAllergies({ patientId })

  dispatch(fetchPatientAllergiesSuccess(allergies))
}

export const fetchPatientExaminations = (patientId: string): AppThunk => async dispatch => {
  dispatch(fetchPatientExaminationsStart())
  let examinations = await PatientDiagnosisRepository.findAllPatientExaminations({ patientId })
  examinations = examinations.map(examination => setExaminationDocument(examination))
  dispatch(fetchPatientExaminationsSuccess(examinations))
}

export const fetchPatientSchedulePlans = (patientId: string): AppThunk => async dispatch => {
  let schedulePlans = await PatientDiagnosisRepository.findAllPatientSchedulePlans({ patientId })
  dispatch(fetchPatientSchedulePlansSuccess(schedulePlans))
}

export const fetchPatientMedicalReferralResults = (
  patientId: string,
): AppThunk => async dispatch => {
  dispatch(fetchPatientMedicalReferralResultsStart())
  const medicalReferralResults = await PatientDiagnosisRepository.findAllPatientMedicalReferralResults(
    { patientId },
  )

  dispatch(fetchPatientMedicalReferralResultsSuccess(medicalReferralResults))
}

export const fetchPatientAppointmentOwner = (patientId: string): AppThunk => async dispatch => {
  const appointmentOwner = await PatientDiagnosisRepository.getAppointmentOwner({ patientId })

  dispatch(fetchPatientAppointmentOwnerSuccess(appointmentOwner))
}

export const updatePatientAppointmentOwner = (
  patientId: string,
  userId: string | null,
): AppThunk => async dispatch => {
  const appointmentOwner = await PatientDiagnosisRepository.setAppointmentOwner(patientId, userId)

  dispatch(fetchPatientAppointmentOwnerSuccess(appointmentOwner))
}

export const createPatientTopic = (
  topic: PatientsDiagnosisTopics,
  firstDiagnosis: PatientsDiagnosis,
): AppThunk => async dispatch => {
  dispatch(createPatientTopicStart())

  const result = await PatientDiagnosisRepository.createTopic(topic, firstDiagnosis)
  dispatch(createPatientTopicSuccess(result))
}

export const createPatientSchedulePlan = (
  examPlan: InternalExaminationPlan,
): AppThunk => async dispatch => {
  const result = await PatientDiagnosisRepository.createInternalExaminationPlan(examPlan)
  dispatch(fetchPatientExaminations(examPlan?.patientId))
  if (result.data.newSchedulePlan)
    dispatch(updatePatientSchedulePlanSuccess(result.data.newSchedulePlan))
}

export const closePatientSchedulePlan = (
  examPlanId: string,
  followUp: PatientDiagnosisDiscussions,
): AppThunk => async dispatch => {
  dispatch(addDiscussionToTopic(followUp))

  const result = await PatientDiagnosisRepository.closeInternalExaminationPlan(
    examPlanId,
    followUp.discussion,
  )
  if (result.data.newSchedulePlan)
    dispatch(updatePatientSchedulePlanSuccess(result.data.newSchedulePlan))
}

export const updateTopicStatus = (topicId: string, status: string): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    const result = await PatientDiagnosisRepository.updateTopicStatus(topicId, status)

    dispatch(updatePatientTopicSuccess(result))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const addDiagnosisToTopic = (
  topicId: string,
  diagnosisToAdd: PatientsDiagnosis,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    const result = await PatientDiagnosisRepository.addDiagnosisToTopic(topicId, diagnosisToAdd)

    dispatch(updatePatientTopicSuccess(result))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const mergeDiagnosisToTopic = (
  topicId: string,
  mergeTopics: PatientsDiagnosis,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    const result = await PatientDiagnosisRepository.mergeTopics(topicId, mergeTopics)
    console.log(result)
    dispatch(updatePatientTopicSuccess(result))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const deleteTopic = (topicId: string): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    await PatientDiagnosisRepository.deleteTopic(topicId)

    dispatch(deleteTopicSuccess({ id: topicId }))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const updateDiagnosisInTopic = (
  topicId: string,
  diagnosisToUpdate: PatientsDiagnosisSimple,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    const result = await PatientDiagnosisRepository.updateDiagnosisInTopic(
      topicId,
      diagnosisToUpdate,
    )

    dispatch(updatePatientTopicSuccess(result))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const addDiscussionToTopic = (
  discussionToAdd: PatientDiagnosisDiscussions,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())
  try {
    const newDiscussion = await PatientDiagnosisRepository.addDiscussionToTopic(discussionToAdd)
    dispatch(updatePatientDiscussionsSuccess(newDiscussion))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const updateDiscussionInTopic = (
  discussionToUpdate: PatientDiagnosisDiscussions,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    const result = await PatientDiagnosisRepository.updateDiscussionInTopic(discussionToUpdate)
    dispatch(updatePatientDiscussionsSuccess(result))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const addMedicationToTopic = (
  medicationToAdd: PatientsDiagnosisMedications,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())
  try {
    const newMedication = await PatientDiagnosisRepository.addMedicationToTopic(medicationToAdd)
    dispatch(updatePatientMedicasionsSuccess(newMedication))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const addAllergy = (
  allergyToAdd: Allergy,
  onSuccess?: () => void,
): AppThunk => async dispatch => {
  try {
    const newAllergy = await PatientDiagnosisRepository.addAllergy(allergyToAdd)
    dispatch(updatePatientAllergiesSuccess(newAllergy))
    if (onSuccess) {
      onSuccess()
    }
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const updatePatientTopic = (
  topic: PatientsDiagnosisTopics,
  onSuccess?: (patientsDiagnosisTopics: PatientsDiagnosisTopics) => void,
  onError?: (error: any) => void,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    const updatedPatientDiagnosis = await PatientDiagnosisRepository.saveOrUpdate(topic)

    dispatch(updatePatientTopicSuccess(updatedPatientDiagnosis as PatientsDiagnosisTopics))
    if (onSuccess) {
      onSuccess(updatedPatientDiagnosis as PatientsDiagnosisTopics)
    }
  } catch (error) {
    dispatch(updatePatientFailed())

    if (onError) {
      onError(error)
    }
  }
}

export const updatePatientMedication = (
  medication: PatientsDiagnosisMedications,
): AppThunk => async dispatch => {
  try {
    const updatedPatientMedication = await PatientDiagnosisRepository.updateMedication(medication)
    dispatch(
      updatePatientMedicasionsSuccess(updatedPatientMedication as PatientsDiagnosisMedications),
    )
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const updatePatientAllergy = (allergy: Allergy): AppThunk => async dispatch => {
  try {
    const updatedPatientAllergy = await PatientDiagnosisRepository.updateAllergy(allergy)
    dispatch(updatePatientAllergiesSuccess(updatedPatientAllergy as Allergy))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export default patientDiagnosisSlice.reducer
